import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { I18nService } from '@app/i18n/services';
import { Endpoint, EndpointService } from '@services/endpoints/';
import { Observable, Subject } from 'rxjs';

import { ObjectString } from '../../../shared/utilities';
import { IUser } from './_models';

@Injectable({
	providedIn: 'root'
})
export class UserService {
	private dataSubject = new Subject<ObjectString<IUser>>();
	public dataObs = this.dataSubject.asObservable();

	private users: { [id: string]: IUser } = {};

	constructor(private http: HttpClient, private i18nService: I18nService, private endpointService: EndpointService) {}

	/**
	 * Clear user cache, also handle clearing individual user from cache.
	 *
	 * @param id Optional user ID, if provided will clear user ID out of cache
	 */
	public clearCache(id?: string): void {
		if (id) {
			delete this.users[id];
		} else {
			this.users = {};
		}
	}

	public displayName(uid: string, authUserId?: string): string {
		if (authUserId && uid === authUserId) {
			return this.i18nService.get('You', 'app.descriptor.you');
		}

		const user = this.users[uid];
		const first = user?.firstname ? user.firstname : '';
		const last = user?.lastname ? user.lastname : '';
		const spacer = first && last ? ' ' : '';

		return `${first}${spacer}${last}`;
	}

	/**
	 * Get user details, if user ID is not provided it will get the authenticated user.
	 *
	 * @param id User ID
	 */
	public getUser(id?: string, force = false): Observable<IUser> {
		return new Observable<IUser>((observer) => {
			if (this.users[id] && !force) {
				observer.next(this.users[id]);
				observer.complete();
			} else {
				this.http.get<IUser>(`${this.endpointService.url(Endpoint.user)}${id ? `/${id}` : ''}`).subscribe(
					(user: IUser) => {
						const cognitoId = user.identities?.['COGNITO'] || user.uuid;
						user.id = cognitoId;
						this.users[cognitoId] = user;
						this.dataSubject.next(this.users);
						observer.next(user);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			}
		});
	}

	/**
	 * Update a selected user. If user ID is not provided it will update authenticated user.
	 *
	 * @param id User ID
	 */
	public updateUser(params: ObjectString<any>, id?: string): Observable<IUser> {
		return new Observable<IUser>((observer) => {
			this.http.post<IUser>(`${this.endpointService.url(Endpoint.user)}${id ? `?uid=${id}` : ''}`, params).subscribe(
				(user: IUser) => {
					const cognitoId = user.identities?.['COGNITO'] || user.uuid;
					user.id = cognitoId;
					this.users[cognitoId] = user;
					this.dataSubject.next(this.users);
					observer.next(user);
					observer.complete();
				},
				(error: any) => {
					observer.next(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Delete a user and clear user from local cache.
	 *
	 * @param id User ID
	 */
	public deleteUser(id: string): Observable<boolean> {
		return new Observable<boolean>((observer) => {
			this.http.delete<any>(`${this.endpointService.url(Endpoint.user)}?uid=${id}`).subscribe(
				() => {
					delete this.users[id];
					this.dataSubject.next(this.users);
					observer.next(true);
					observer.complete();
				},
				(error: any) => {
					if (error.status === 403) {
						observer.next(false);
					} else {
						observer.error(error);
					}
					observer.complete();
				}
			);
		});
	}

	/**
	 * Validate if a user with provided email already exists.
	 *
	 * @param email Email to validate
	 */
	public userExists(email: string): Observable<boolean> {
		return this.http.get<boolean>(`${this.endpointService.url(Endpoint.authExists)}?email=${encodeURIComponent(email)}`);
	}
}
